import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { 
  Play, 
  CheckCircle,
  Lock,
  ArrowLeft,
  Clock,
  VideoCamera,
  Calendar,
  ArrowRight,
  FileText,
  FilePdf
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isUploadedVideo = (url) => url && url.startsWith('/api/uploads');
const isEmbedUrl = (url) => url && (url.includes('youtube.com/embed') || url.includes('vimeo.com'));

const getFullVideoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads')) {
    return `${API_URL}${url}`;
  }
  return url;
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({ completed_modules: [], quiz_scores: {} });
  const [liveSessions, setLiveSessions] = useState([]);
  const [onDemandVideos, setOnDemandVideos] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('modules');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourseData = useCallback(async () => {
    try {
      const [courseRes, liveRes, onDemandRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/live-sessions`),
        api.get(`/courses/${courseId}/on-demand`)
      ]);
      
      setCourse(courseRes.data);
      setLiveSessions(liveRes.data);
      setOnDemandVideos(onDemandRes.data);
      
      if (courseRes.data.modules?.length > 0) {
        setActiveModule(courseRes.data.modules[0]);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('upgrade');
      } else {
        setError('notfound');
      }
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const fetchProgress = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get(`/courses/${courseId}/progress`);
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  }, [courseId, isAuthenticated]);

  useEffect(() => {
    fetchCourseData();
    fetchProgress();
  }, [fetchCourseData, fetchProgress]);

  const markModuleComplete = async (moduleId) => {
    if (!isAuthenticated) return;
    
    const newCompletedModules = progress.completed_modules.includes(moduleId)
      ? progress.completed_modules
      : [...progress.completed_modules, moduleId];

    try {
      await api.post(`/courses/${courseId}/progress`, {
        completed_modules: newCompletedModules,
        quiz_scores: progress.quiz_scores
      });
      
      setProgress(prev => ({ ...prev, completed_modules: newCompletedModules }));
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const upcomingSessions = liveSessions.filter(s => 
    s.status === 'scheduled' || s.status === 'live'
  ).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error === 'upgrade') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Lock className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" weight="duotone" />
          <h2 className="font-outfit text-2xl text-white mb-2">Course Locked</h2>
          <p className="text-zinc-400 mb-6">Upgrade your subscription to access this course.</p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/courses')} className="border-zinc-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Courses
            </Button>
            <Link to="/pricing">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'notfound' || !course) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="font-outfit text-2xl text-white mb-2">Course Not Found</h2>
          <p className="text-zinc-400 mb-6">The course you're looking for doesn't exist.</p>
          <Button variant="outline" onClick={() => navigate('/courses')} className="border-zinc-700 text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  const completionPercent = course.modules?.length 
    ? Math.round((progress.completed_modules.length / course.modules.length) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="course-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/courses')}
          className="text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Courses
        </Button>

        {/* Course Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121212] border border-white/5 p-6 mb-6"
        >
          <h1 className="font-outfit text-2xl sm:text-3xl text-white mb-2">{course.title}</h1>
          <p className="text-zinc-400 mb-4">{course.description}</p>
          
          {isAuthenticated && (
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-zinc-500">Course Progress</span>
                <span className="text-[#D4AF37] font-mono">{completionPercent}%</span>
              </div>
              <Progress value={completionPercent} className="h-2 bg-zinc-800" />
            </div>
          )}
        </motion.div>

        {/* Upcoming Live Session Banner */}
        {upcomingSessions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-red-500/20 to-[#D4AF37]/20 border border-red-500/30 p-4 mb-6"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${upcomingSessions[0].status === 'live' ? 'bg-red-500 animate-pulse' : 'bg-[#D4AF37]'}`} />
                <div>
                  <p className="text-white font-medium">
                    {upcomingSessions[0].status === 'live' ? 'LIVE NOW: ' : 'Next Live Session: '}
                    {upcomingSessions[0].title}
                  </p>
                  <p className="text-sm text-zinc-400">{formatDate(upcomingSessions[0].scheduled_date)}</p>
                </div>
              </div>
              {upcomingSessions[0].zoom_link && !upcomingSessions[0].is_locked && (
                <a href={upcomingSessions[0].zoom_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-red-500 text-white hover:bg-red-600">
                    <VideoCamera className="w-4 h-4 mr-2" weight="fill" />
                    {upcomingSessions[0].status === 'live' ? 'Join Now' : 'Join Meeting'}
                  </Button>
                </a>
              )}
              {upcomingSessions[0].is_locked && (
                <Link to="/pricing">
                  <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                    <Lock className="w-4 h-4 mr-2" />
                    Upgrade to Join
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        )}

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/5">
            <TabsTrigger value="modules" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Course Modules
            </TabsTrigger>
            <TabsTrigger value="live" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Live Sessions
              {upcomingSessions.length > 0 && (
                <span className="ml-2 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </TabsTrigger>
            <TabsTrigger value="on-demand" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              On-Demand
            </TabsTrigger>
          </TabsList>

          {/* Modules Tab */}
          <TabsContent value="modules">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Video Player */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="bg-[#121212] border border-white/5 mb-6">
                    <div className="video-container">
                      {activeModule?.video_url ? (
                        isUploadedVideo(activeModule.video_url) ? (
                          <video
                            key={activeModule.video_url}
                            controls
                            controlsList="nodownload"
                            className="absolute inset-0 w-full h-full bg-black"
                            data-testid="uploaded-video-player"
                          >
                            <source src={getFullVideoUrl(activeModule.video_url)} type="video/mp4" />
                            Your browser does not support video playback.
                          </video>
                        ) : isEmbedUrl(activeModule.video_url) ? (
                          <iframe
                            src={activeModule.video_url}
                            title={activeModule.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video
                            key={activeModule.video_url}
                            controls
                            className="absolute inset-0 w-full h-full bg-black"
                            data-testid="external-video-player"
                          >
                            <source src={activeModule.video_url} />
                            Your browser does not support video playback.
                          </video>
                        )
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
                          <p className="text-zinc-500">Select a module to start</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-outfit text-xl text-white">
                        {activeModule?.title || 'Select a Module'}
                      </h2>
                      {activeModule && isAuthenticated && (
                        <Button
                          variant={progress.completed_modules.includes(activeModule.id) ? "outline" : "default"}
                          onClick={() => markModuleComplete(activeModule.id)}
                          className={progress.completed_modules.includes(activeModule.id) 
                            ? "border-green-500 text-green-500" 
                            : "bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                          }
                        >
                          {progress.completed_modules.includes(activeModule.id) ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" weight="fill" />
                              Completed
                            </>
                          ) : (
                            'Mark Complete'
                          )}
                        </Button>
                      )}
                    </div>
                    {activeModule?.duration && (
                      <p className="text-sm text-zinc-400 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {activeModule.duration} minutes
                      </p>
                    )}

                    {/* Text Content */}
                    {activeModule?.text_content && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2 mb-3">
                          <FileText className="w-4 h-4 text-[#D4AF37]" />
                          Lesson Notes
                        </h3>
                        <div className="text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap" data-testid="module-text-content">
                          {activeModule.text_content}
                        </div>
                      </div>
                    )}

                    {/* PDF Resources */}
                    {activeModule?.pdf_urls?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2 mb-3">
                          <FilePdf className="w-4 h-4 text-[#D4AF37]" />
                          Resources
                        </h3>
                        <div className="space-y-2">
                          {activeModule.pdf_urls.map((pdf, i) => (
                            <a
                              key={i}
                              href={pdf.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-3 bg-black/30 border border-white/5 hover:border-[#D4AF37]/30 transition-colors group"
                              data-testid={`pdf-link-${i}`}
                            >
                              <FilePdf className="w-5 h-5 text-red-400 flex-shrink-0" weight="duotone" />
                              <span className="text-sm text-zinc-300 group-hover:text-[#D4AF37] flex-1 truncate">
                                {pdf.title || 'Download PDF'}
                              </span>
                              <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-[#D4AF37]" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Modules List */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[#121212] border border-white/5 p-6"
                >
                  <h3 className="font-outfit text-lg text-white mb-4">Modules</h3>
                  <div className="space-y-2">
                    {course.modules?.map((module, index) => {
                      const isCompleted = progress.completed_modules.includes(module.id);
                      const isActive = activeModule?.id === module.id;
                      
                      return (
                        <button
                          key={module.id}
                          onClick={() => setActiveModule(module)}
                          className={`w-full text-left p-4 border transition-all ${
                            isActive 
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                              : 'border-white/5 hover:border-white/20'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 flex items-center justify-center ${
                              isCompleted 
                                ? 'bg-green-500/20 text-green-500' 
                                : isActive 
                                ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                                : 'bg-zinc-800 text-zinc-500'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle className="w-4 h-4" weight="fill" />
                              ) : (
                                <Play className="w-4 h-4" weight="fill" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isActive ? 'text-[#D4AF37]' : 'text-white'}`}>
                                {index + 1}. {module.title}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {module.duration && (
                                  <p className="text-xs text-zinc-500">{module.duration} min</p>
                                )}
                                {module.video_url && (
                                  <span className={`text-xs ${isUploadedVideo(module.video_url) ? 'text-green-600' : 'text-zinc-600'}`}>
                                    {isUploadedVideo(module.video_url) ? 'Uploaded' : ''}
                                  </span>
                                )}
                                {module.text_content && <FileText className="w-3 h-3 text-zinc-600" />}
                                {module.pdf_urls?.length > 0 && <FilePdf className="w-3 h-3 text-zinc-600" />}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            </div>
          </TabsContent>

          {/* Live Sessions Tab */}
          <TabsContent value="live">
            <div className="space-y-6">
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-outfit text-xl text-white mb-2">Live Trading Sessions</h3>
                <p className="text-zinc-400 mb-4">
                  Join us every <span className="text-[#D4AF37] font-medium">Thursday at 8:00 PM EST</span> for live market analysis and Q&A.
                </p>
              </div>

              {liveSessions.length === 0 ? (
                <div className="bg-[#121212] border border-white/5 p-12 text-center">
                  <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" weight="duotone" />
                  <p className="text-zinc-400">No live sessions scheduled yet.</p>
                  <p className="text-sm text-zinc-500 mt-2">Check back soon!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {liveSessions.map((session) => (
                    <div 
                      key={session.session_id}
                      className={`bg-[#121212] border p-6 relative ${
                        session.status === 'live' 
                          ? 'border-red-500/50' 
                          : session.status === 'completed'
                          ? 'border-white/5 opacity-60'
                          : 'border-white/10'
                      }`}
                    >
                      {session.is_locked && (
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                          <Lock className="w-8 h-8 text-[#D4AF37] mb-2" weight="duotone" />
                          <p className="text-sm text-zinc-400 mb-3">Upgrade to access</p>
                          <Link to="/pricing">
                            <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                              Upgrade <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      )}

                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {session.status === 'live' && (
                            <span className="flex items-center gap-1 text-xs bg-red-500 text-white px-2 py-1">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              LIVE
                            </span>
                          )}
                          {session.status === 'scheduled' && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1">UPCOMING</span>
                          )}
                          {session.status === 'completed' && (
                            <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-1">ENDED</span>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 ${
                          session.tier_required === 'elite' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                          session.tier_required === 'premium' ? 'bg-white/10 text-white' :
                          'bg-zinc-800 text-zinc-400'
                        }`}>
                          {session.tier_required}
                        </span>
                      </div>

                      <h4 className="font-outfit text-lg text-white mb-2">{session.title}</h4>
                      {session.description && (
                        <p className="text-sm text-zinc-400 mb-3">{session.description}</p>
                      )}

                      <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
                        <Calendar className="w-4 h-4" />
                        {formatDate(session.scheduled_date)}
                      </div>

                      {session.zoom_link && (session.status === 'scheduled' || session.status === 'live') && (
                        <div className="border-t border-white/5 pt-4">
                          <a href={session.zoom_link} target="_blank" rel="noopener noreferrer">
                            <Button className={`w-full ${
                              session.status === 'live' 
                                ? 'bg-red-500 hover:bg-red-600' 
                                : 'bg-[#D4AF37] hover:bg-[#E5C158]'
                            } text-black`}>
                              <VideoCamera className="w-4 h-4 mr-2" weight="fill" />
                              {session.status === 'live' ? 'Join Live Session' : 'Join When Live'}
                            </Button>
                          </a>
                          {session.zoom_meeting_id && (
                            <p className="text-xs text-zinc-500 mt-2 text-center">
                              Meeting ID: {session.zoom_meeting_id}
                              {session.zoom_passcode && ` • Passcode: ${session.zoom_passcode}`}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* On-Demand Tab */}
          <TabsContent value="on-demand">
            <div className="space-y-6">
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-outfit text-xl text-white mb-2">On-Demand Videos</h3>
                <p className="text-zinc-400">
                  Watch recordings of previous live sessions at your convenience.
                </p>
              </div>

              {onDemandVideos.length === 0 ? (
                <div className="bg-[#121212] border border-white/5 p-12 text-center">
                  <Play className="w-12 h-12 text-zinc-600 mx-auto mb-4" weight="duotone" />
                  <p className="text-zinc-400">No recordings available yet.</p>
                  <p className="text-sm text-zinc-500 mt-2">Previous live sessions will appear here.</p>
                </div>
              ) : (
                <>
                  {/* Video Player */}
                  {activeVideo && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#121212] border border-white/5"
                    >
                      <div className="video-container">
                        <iframe
                          src={activeVideo.video_url}
                          title={activeVideo.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-outfit text-lg text-white mb-1">{activeVideo.title}</h4>
                        <p className="text-sm text-zinc-400">{activeVideo.description}</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Video Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {onDemandVideos.map((video) => (
                      <div 
                        key={video.video_id}
                        className={`bg-[#121212] border overflow-hidden cursor-pointer group relative ${
                          activeVideo?.video_id === video.video_id 
                            ? 'border-[#D4AF37]' 
                            : 'border-white/5 hover:border-white/20'
                        }`}
                        onClick={() => !video.is_locked && setActiveVideo(video)}
                      >
                        {video.is_locked && (
                          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                            <Lock className="w-8 h-8 text-[#D4AF37] mb-2" weight="duotone" />
                            <Link to="/pricing">
                              <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                                Upgrade
                              </Button>
                            </Link>
                          </div>
                        )}

                        <div className="relative h-36 bg-zinc-900">
                          {video.thumbnail_url ? (
                            <img 
                              src={video.thumbnail_url} 
                              alt={video.title} 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="w-12 h-12 text-zinc-700 group-hover:text-[#D4AF37] transition-colors" weight="fill" />
                            </div>
                          )}
                          {video.duration_minutes && (
                            <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1">
                              {video.duration_minutes} min
                            </span>
                          )}
                        </div>

                        <div className="p-4">
                          <h4 className="font-medium text-white line-clamp-1 group-hover:text-[#D4AF37] transition-colors">
                            {video.title}
                          </h4>
                          {video.original_air_date && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Originally aired: {new Date(video.original_air_date).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric' 
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CourseDetail;
